import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import {
  listUsers, createUser, getUserDetail, updateUser, disableUser, enableUser,
  resendInvite, adminResetUserPassword,
} from "@/lib/api";
import {
  Loader2, UserPlus, Search, Copy, Shield, Mail, KeyRound, Ban, CheckCircle2,
  RefreshCw, Clock, Link2,
} from "lucide-react";

const ROLE_OPTIONS = [
  { key: "super_admin", label: "Super Admin" },
  { key: "admin", label: "Admin" },
  { key: "sales", label: "Sales / Business Development" },
  { key: "recruiter", label: "Recruiter / Talent" },
];
const roleLabel = (k) => (ROLE_OPTIONS.find((r) => r.key === k) || {}).label || k;

const StatusBadge = ({ status }) => {
  const map = {
    Active: "bg-[#2EE59D]/15 text-[#2EE59D] border-[#2EE59D]/30",
    Invited: "bg-[#F5B94A]/15 text-[#F5B94A] border-[#F5B94A]/30",
    Disabled: "bg-white/10 text-white/50 border-white/15",
  };
  return (
    <Badge className={`${map[status] || map.Disabled} border rounded-md`} data-testid="user-status-badge">
      {status}
    </Badge>
  );
};

const fmtDate = (iso) => {
  if (!iso) return "\u2014";
  try {
    return new Date(iso).toLocaleString("en-US", {
      month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return String(iso).slice(0, 16);
  }
};

const EMPTY_FORM = { first_name: "", last_name: "", email: "", role: "admin", job_title: "", notes: "" };

export const UsersManager = ({ token, me }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [creating, setCreating] = useState(false);
  const [inviteLink, setInviteLink] = useState(null); // {link, email, emailSent}

  const [detail, setDetail] = useState(null); // {user, activity}
  const [detailOpen, setDetailOpen] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);
  const [edit, setEdit] = useState(null); // editable copy in detail
  const [actionBusy, setActionBusy] = useState("");
  const [disableTarget, setDisableTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listUsers(token, {
        search: search || undefined, role: roleFilter, status: statusFilter,
      });
      setUsers(data.users);
    } catch {
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  }, [token, search, roleFilter, statusFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const submitCreate = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.last_name || !form.email) {
      toast.error("First name, last name, and email are required.");
      return;
    }
    setCreating(true);
    try {
      const res = await createUser(token, form);
      setAddOpen(false);
      setForm(EMPTY_FORM);
      setInviteLink({ link: res.invite_link, email: res.user.email, emailSent: res.email_sent });
      toast.success(res.email_sent ? "User invited \u2014 email sent" : "User created \u2014 share the invite link");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create user");
    } finally {
      setCreating(false);
    }
  };

  const openDetail = async (id) => {
    setDetailOpen(true);
    setDetail(null);
    try {
      const d = await getUserDetail(token, id);
      setDetail(d);
      setEdit({
        first_name: d.user.first_name || "",
        last_name: d.user.last_name || "",
        role: d.user.role,
        job_title: d.user.job_title || "",
        notes: d.user.notes || "",
      });
    } catch {
      toast.error("Failed to load user");
      setDetailOpen(false);
    }
  };

  const saveEdit = async () => {
    setSavingEdit(true);
    try {
      const res = await updateUser(token, detail.user.id, edit);
      setDetail((prev) => ({ ...prev, user: res.user }));
      toast.success("User updated");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    } finally {
      setSavingEdit(false);
    }
  };

  const doAction = async (kind, user) => {
    setActionBusy(kind + user.id);
    try {
      if (kind === "enable") {
        const res = await enableUser(token, user.id);
        setDetail((p) => (p ? { ...p, user: res.user } : p));
        toast.success("User enabled");
      } else if (kind === "resend") {
        const res = await resendInvite(token, user.id);
        setInviteLink({ link: res.invite_link, email: user.email, emailSent: res.email_sent });
        toast.success(res.email_sent ? "Invitation re-sent" : "New invite link generated");
      } else if (kind === "reset") {
        const res = await adminResetUserPassword(token, user.id);
        setInviteLink({ link: res.reset_link, email: user.email, emailSent: res.email_sent, reset: true });
        toast.success(res.email_sent ? "Reset link sent" : "Reset link generated");
      }
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Action failed");
    } finally {
      setActionBusy("");
    }
  };

  const confirmDisable = async () => {
    const user = disableTarget;
    setActionBusy("disable" + user.id);
    try {
      const res = await disableUser(token, user.id);
      setDetail((p) => (p ? { ...p, user: res.user } : p));
      toast.success("User disabled");
      setDisableTarget(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not disable user");
    } finally {
      setActionBusy("");
    }
  };

  const copyLink = (link) => {
    navigator.clipboard?.writeText(link).then(
      () => toast.success("Link copied to clipboard"),
      () => toast.error("Copy failed \u2014 select and copy manually")
    );
  };

  return (
    <div data-testid="users-manager">
      {/* Header + filters */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-6">
        <div>
          <h2 className="text-xl font-bold flex items-center gap-2">
            <Shield size={18} className="text-[#66B2FF]" /> Admin Users
          </h2>
          <p className="text-white/50 text-sm">Invite teammates and manage their access.</p>
        </div>
        <Button
          data-testid="users-add-button"
          onClick={() => { setForm(EMPTY_FORM); setAddOpen(true); }}
          className="sm:ml-auto bg-[#1E6BFF] hover:bg-[#3B82F6] text-white rounded-xl h-11 px-5"
        >
          <UserPlus size={16} className="mr-2" /> Add User
        </Button>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
          <Input
            data-testid="users-search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name or email"
            className="bg-[#0F1424] border-white/10 h-11 text-white pl-9"
          />
        </div>
        <Select value={roleFilter} onValueChange={setRoleFilter}>
          <SelectTrigger data-testid="users-role-filter" className="bg-[#0F1424] border-white/10 h-11 text-white w-full sm:w-56">
            <SelectValue placeholder="Role" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All roles</SelectItem>
            {ROLE_OPTIONS.map((r) => (
              <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger data-testid="users-status-filter" className="bg-[#0F1424] border-white/10 h-11 text-white w-full sm:w-40">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="Active">Active</SelectItem>
            <SelectItem value="Invited">Invited</SelectItem>
            <SelectItem value="Disabled">Disabled</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div className="rounded-2xl border border-white/10 overflow-hidden" style={{ backgroundColor: "#0F1424" }}>
        {loading ? (
          <div className="flex items-center justify-center py-20 text-white/50">
            <Loader2 className="animate-spin mr-2" size={20} /> Loading users&hellip;
          </div>
        ) : users.length === 0 ? (
          <div className="text-center py-16 text-white/50" data-testid="users-empty-state">No users found.</div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-white/10 hover:bg-transparent">
                  <TableHead className="text-white/60">Name</TableHead>
                  <TableHead className="text-white/60">Email</TableHead>
                  <TableHead className="text-white/60">Role</TableHead>
                  <TableHead className="text-white/60">Status</TableHead>
                  <TableHead className="text-white/60 hidden lg:table-cell">Last Login</TableHead>
                  <TableHead className="text-white/60 hidden lg:table-cell">Added By</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((u) => (
                  <TableRow
                    key={u.id}
                    data-testid={`user-row-${u.id}`}
                    onClick={() => openDetail(u.id)}
                    className="border-white/10 cursor-pointer hover:bg-white/5"
                  >
                    <TableCell className="font-medium text-white">
                      {u.name}
                      {u.job_title ? <div className="text-white/40 text-xs">{u.job_title}</div> : null}
                    </TableCell>
                    <TableCell className="text-white/70">{u.email}</TableCell>
                    <TableCell className="text-white/80">{u.role_label || roleLabel(u.role)}</TableCell>
                    <TableCell><StatusBadge status={u.status} /></TableCell>
                    <TableCell className="text-white/50 hidden lg:table-cell">{fmtDate(u.last_login_at)}</TableCell>
                    <TableCell className="text-white/50 hidden lg:table-cell">{u.added_by_name || "\u2014"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
      <p className="text-white/40 text-xs mt-3">{users.length} user(s)</p>

      {/* Add User dialog */}
      <Dialog open={addOpen} onOpenChange={(o) => !creating && setAddOpen(o)}>
        <DialogContent className="bg-[#0F1424] border-white/10 text-white max-w-lg" data-testid="users-add-dialog">
          <DialogHeader>
            <DialogTitle className="text-white">Invite a new user</DialogTitle>
          </DialogHeader>
          <form onSubmit={submitCreate} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-white/80 mb-1.5 block">First name</Label>
                <Input data-testid="users-first-name" value={form.first_name}
                  onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                  className="bg-[#0A0F1E] border-white/10 h-11 text-white" />
              </div>
              <div>
                <Label className="text-white/80 mb-1.5 block">Last name</Label>
                <Input data-testid="users-last-name" value={form.last_name}
                  onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                  className="bg-[#0A0F1E] border-white/10 h-11 text-white" />
              </div>
            </div>
            <div>
              <Label className="text-white/80 mb-1.5 block">Email address</Label>
              <Input data-testid="users-email" type="email" value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="bg-[#0A0F1E] border-white/10 h-11 text-white" />
            </div>
            <div>
              <Label className="text-white/80 mb-1.5 block">Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="users-role-select" className="bg-[#0A0F1E] border-white/10 h-11 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ROLE_OPTIONS.map((r) => (
                    <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-white/80 mb-1.5 block">Job title <span className="text-white/40">(optional)</span></Label>
                <Input data-testid="users-job-title" value={form.job_title}
                  onChange={(e) => setForm({ ...form, job_title: e.target.value })}
                  className="bg-[#0A0F1E] border-white/10 h-11 text-white" />
              </div>
              <div>
                <Label className="text-white/80 mb-1.5 block">Notes <span className="text-white/40">(optional)</span></Label>
                <Input data-testid="users-notes" value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  className="bg-[#0A0F1E] border-white/10 h-11 text-white" />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setAddOpen(false)}
                className="text-white/70 hover:text-white hover:bg-white/5">Cancel</Button>
              <Button type="submit" disabled={creating} data-testid="users-create-submit"
                className="bg-[#1E6BFF] hover:bg-[#3B82F6] text-white">
                {creating ? <Loader2 className="animate-spin mr-2" size={16} /> : <Mail size={16} className="mr-2" />}
                Send invitation
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Invite/reset link dialog (copy-to-share fallback) */}
      <Dialog open={!!inviteLink} onOpenChange={(o) => !o && setInviteLink(null)}>
        <DialogContent className="bg-[#0F1424] border-white/10 text-white max-w-lg" data-testid="invite-link-dialog">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Link2 size={18} className="text-[#66B2FF]" />
              {inviteLink?.reset ? "Password reset link" : "Invitation link"}
            </DialogTitle>
          </DialogHeader>
          <p className="text-white/60 text-sm">
            {inviteLink?.emailSent
              ? `An email was sent to ${inviteLink?.email}. You can also copy the secure link below to share it directly.`
              : `Email delivery is unavailable, so share this single-use secure link with ${inviteLink?.email} directly.`}
          </p>
          <div className="flex items-center gap-2 mt-2">
            <Input readOnly value={inviteLink?.link || ""} data-testid="invite-link-value"
              className="bg-[#0A0F1E] border-white/10 h-11 text-white text-xs" />
            <Button data-testid="invite-copy-button" onClick={() => copyLink(inviteLink?.link)}
              className="bg-[#1E6BFF] hover:bg-[#3B82F6] text-white h-11 shrink-0">
              <Copy size={15} className="mr-1.5" /> Copy
            </Button>
          </div>
          <p className="text-white/40 text-xs mt-1">This link is single-use and expires for security.</p>
        </DialogContent>
      </Dialog>

      {/* User detail dialog */}
      <Dialog open={detailOpen} onOpenChange={(o) => !o && setDetailOpen(false)}>
        <DialogContent className="bg-[#0F1424] border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="user-detail-dialog">
          {!detail ? (
            <div className="flex items-center justify-center py-16 text-white/50">
              <Loader2 className="animate-spin mr-2" size={20} /> Loading&hellip;
            </div>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle className="text-white flex items-center gap-3">
                  {detail.user.name}
                  <StatusBadge status={detail.user.status} />
                </DialogTitle>
              </DialogHeader>

              <div className="grid sm:grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="text-white/40 text-xs">Email</div>
                  <div className="text-white/85" data-testid="user-detail-email">{detail.user.email}</div>
                </div>
                <div>
                  <div className="text-white/40 text-xs">Added by</div>
                  <div className="text-white/85">{detail.user.added_by_name || "\u2014"}</div>
                </div>
                <div>
                  <div className="text-white/40 text-xs">Date added</div>
                  <div className="text-white/85">{fmtDate(detail.user.created_at)}</div>
                </div>
                <div>
                  <div className="text-white/40 text-xs">Last login</div>
                  <div className="text-white/85">{fmtDate(detail.user.last_login_at)}</div>
                </div>
              </div>

              {/* Edit form */}
              <div className="mt-2 rounded-xl border border-white/10 p-4">
                <div className="text-white/70 text-sm font-medium mb-3">Edit user</div>
                <div className="grid grid-cols-2 gap-3">
                  <Input value={edit.first_name} onChange={(e) => setEdit({ ...edit, first_name: e.target.value })}
                    placeholder="First name" className="bg-[#0A0F1E] border-white/10 h-10 text-white" data-testid="user-edit-first" />
                  <Input value={edit.last_name} onChange={(e) => setEdit({ ...edit, last_name: e.target.value })}
                    placeholder="Last name" className="bg-[#0A0F1E] border-white/10 h-10 text-white" data-testid="user-edit-last" />
                </div>
                <div className="mt-3">
                  <Label className="text-white/60 text-xs mb-1.5 block">Role</Label>
                  <Select value={edit.role} onValueChange={(v) => setEdit({ ...edit, role: v })}>
                    <SelectTrigger data-testid="user-edit-role-select" className="bg-[#0A0F1E] border-white/10 h-10 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ROLE_OPTIONS.map((r) => (
                        <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3 mt-3">
                  <Input value={edit.job_title} onChange={(e) => setEdit({ ...edit, job_title: e.target.value })}
                    placeholder="Job title" className="bg-[#0A0F1E] border-white/10 h-10 text-white" data-testid="user-edit-jobtitle" />
                  <Input value={edit.notes} onChange={(e) => setEdit({ ...edit, notes: e.target.value })}
                    placeholder="Notes" className="bg-[#0A0F1E] border-white/10 h-10 text-white" data-testid="user-edit-notes" />
                </div>
                <div className="flex justify-end mt-3">
                  <Button onClick={saveEdit} disabled={savingEdit} data-testid="user-save-edit"
                    className="bg-[#1E6BFF] hover:bg-[#3B82F6] text-white h-10">
                    {savingEdit ? <Loader2 className="animate-spin mr-2" size={15} /> : <CheckCircle2 size={15} className="mr-2" />}
                    Save changes
                  </Button>
                </div>
              </div>

              {/* Actions */}
              <div className="flex flex-wrap gap-2 mt-2">
                {detail.user.status === "Invited" && (
                  <Button variant="ghost" data-testid="user-action-resend"
                    disabled={actionBusy === "resend" + detail.user.id}
                    onClick={() => doAction("resend", detail.user)}
                    className="bg-white/5 hover:bg-white/10 text-white/80 h-10">
                    <Mail size={15} className="mr-1.5" /> Resend Invitation
                  </Button>
                )}
                {detail.user.status !== "Invited" && (
                  <Button variant="ghost" data-testid="user-action-reset"
                    disabled={actionBusy === "reset" + detail.user.id}
                    onClick={() => doAction("reset", detail.user)}
                    className="bg-white/5 hover:bg-white/10 text-white/80 h-10">
                    <KeyRound size={15} className="mr-1.5" /> Send Password Reset
                  </Button>
                )}
                {detail.user.status === "Disabled" ? (
                  <Button data-testid="user-action-enable"
                    disabled={actionBusy === "enable" + detail.user.id}
                    onClick={() => doAction("enable", detail.user)}
                    className="bg-[#2EE59D]/15 border border-[#2EE59D]/30 text-[#2EE59D] hover:bg-[#2EE59D]/25 h-10">
                    <CheckCircle2 size={15} className="mr-1.5" /> Enable User
                  </Button>
                ) : (
                  <Button data-testid="user-action-disable"
                    onClick={() => setDisableTarget(detail.user)}
                    className="bg-[#FF5C7A]/15 border border-[#FF5C7A]/40 text-[#FF5C7A] hover:bg-[#FF5C7A]/25 h-10">
                    <Ban size={15} className="mr-1.5" /> Disable User
                  </Button>
                )}
              </div>

              {/* Recent activity */}
              <div className="mt-2">
                <div className="text-white/70 text-sm font-medium mb-2 flex items-center gap-2">
                  <Clock size={14} /> Recent activity
                </div>
                <div className="rounded-xl border border-white/10 divide-y divide-white/5 max-h-56 overflow-y-auto">
                  {(detail.activity || []).length === 0 ? (
                    <div className="text-white/40 text-sm px-4 py-3">No activity yet.</div>
                  ) : (
                    detail.activity.map((a) => (
                      <div key={a.id} className="px-4 py-2.5 text-sm flex items-center justify-between gap-3">
                        <span className="text-white/80">
                          {a.action}
                          {a.target_label ? <span className="text-white/50"> &mdash; {a.target_label}</span> : null}
                        </span>
                        <span className="text-white/40 text-xs shrink-0">{fmtDate(a.timestamp)}</span>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Disable confirmation */}
      <AlertDialog open={!!disableTarget} onOpenChange={(o) => !o && setDisableTarget(null)}>
        <AlertDialogContent className="bg-[#0F1424] border-white/10 text-white" data-testid="user-disable-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Disable this user?</AlertDialogTitle>
            <AlertDialogDescription className="text-white/60">
              <span className="text-white font-medium">{disableTarget?.name}</span> will immediately lose
              access to the Admin Dashboard. Their history is retained and they can be re-enabled later.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-white/15 text-white/80 hover:bg-white/5 hover:text-white">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="user-disable-confirm"
              onClick={(e) => { e.preventDefault(); confirmDisable(); }}
              className="bg-[#FF5C7A] hover:bg-[#ff456a] text-white">
              <Ban size={15} className="mr-2" /> Disable user
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default UsersManager;
